import { useState } from 'react';
import { Modal, Stack, Group, Button, NumberInput, Alert, Text } from '@mantine/core';
import { api } from '../../api/client';

// Shipping is a single quotation-level line (stored in quotations.shipping_cost).
// This modal either sets it for the first time or edits the existing value.
export default function AddShippingModal({
  orderId,
  quotationId,
  initialValue,
  onClose,
  onDone,
}: {
  orderId: number;
  quotationId: number;
  initialValue?: number | null;
  onClose: () => void;
  onDone: () => void;
}) {
  const [amount, setAmount] = useState<number | string>(initialValue ?? '');
  const [err, setErr] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  async function save() {
    setErr(null);
    const value = Number(amount);
    if (!Number.isFinite(value) || value < 0) return setErr('Enter a non-negative shipping cost.');
    setBusy(true);
    try {
      await api.put(`/orders/${orderId}/quotations/${quotationId}`, { shippingCost: value });
      onDone();
    } catch (e: any) {
      setErr(e.message ?? 'Save failed');
    } finally {
      setBusy(false);
    }
  }

  async function clearShipping() {
    setBusy(true);
    try {
      await api.put(`/orders/${orderId}/quotations/${quotationId}`, { shippingCost: 0 });
      onDone();
    } catch (e: any) {
      setErr(e.message ?? 'Clear failed');
    } finally {
      setBusy(false);
    }
  }

  return (
    <Modal opened onClose={onClose} title={<span className="modal-title-custom">{initialValue ? 'Edit Shipping' : 'Add Shipping to Quotation'}</span>}>
      <Stack>
        <Text size="sm" c="dimmed">
          Shipping is a single line on the quotation. It is added to both totals with 0% markup.
        </Text>
        <NumberInput
          label="Shipping cost (EUR)"
          data-tour="ship-cost"
          value={amount}
          onChange={setAmount}
          min={0}
          step={1}
          decimalScale={2}
          autoFocus
        />
        {err && <Alert color="red">{err}</Alert>}
        <Group justify="space-between" mt="md">
          <div>
            {!!initialValue && (
              <Button variant="subtle" color="red" onClick={clearShipping} loading={busy}>Remove shipping</Button>
            )}
          </div>
          <Group>
            <Button variant="default" onClick={onClose}>Cancel</Button>
            <Button data-tour="ship-save" onClick={save} loading={busy}>Save</Button>
          </Group>
        </Group>
      </Stack>
    </Modal>
  );
}
